/**
 * useComposerAttachments — one hook for every "Merlin box" composer.
 *
 * Owns the staged `File[]` for the NEXT message plus the three intake
 * affordances Jess asked for: drag-and-drop onto the composer, paste (a
 * screenshot pastes as an image File), and the tray's picker. Pages upload the
 * staged files right before posting the message (the QCP pattern), then clear.
 *
 * Drag-and-drop gotcha (PR #795 / EmailComposeModal): `preventDefault` on the
 * drop zone alone is NOT enough — a drop landing anywhere else on the page
 * makes the browser NAVIGATE to the file, killing the app. While mounted, the
 * hook therefore suppresses dragover/drop at the document level; the zone's own
 * handlers then opt-in the actual drop.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent as ReactClipboardEvent, DragEvent as ReactDragEvent } from 'react';
import { clipboardFiles, dragHasFiles, screenFiles } from './composerAttachments';

export interface DropZoneProps {
  onDragEnter: (e: ReactDragEvent<HTMLElement>) => void;
  onDragOver: (e: ReactDragEvent<HTMLElement>) => void;
  onDragLeave: (e: ReactDragEvent<HTMLElement>) => void;
  onDrop: (e: ReactDragEvent<HTMLElement>) => void;
}

export interface ComposerAttachments {
  /** Files staged for the next message, in the order they were added. */
  files: File[];
  /** Last screening problem ("x.zip: unsupported file type."), or null. */
  attachError: string | null;
  /** True while a file drag hovers the drop zone (drives the highlight). */
  isDragging: boolean;
  addFiles: (incoming: FileList | File[] | null) => void;
  removeFile: (index: number) => void;
  clear: () => void;
  /** Wire onto the composer's textarea — captures pasted screenshots/files. */
  onPaste: (e: ReactClipboardEvent<HTMLElement>) => void;
  /** Spread onto the drop-zone element (usually the whole composer). */
  dropZoneProps: DropZoneProps;
}

export function useComposerAttachments(opts?: { disabled?: boolean }): ComposerAttachments {
  const disabled = opts?.disabled ?? false;
  const [files, setFiles] = useState<File[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // dragenter/dragleave fire for every child the drag crosses — depth-count so
  // the highlight doesn't flicker off while still inside the zone.
  const dragDepth = useRef(0);

  // Document-level guard: without it, a drop that misses the zone navigates the
  // whole SPA to the dropped file (the PR #795 failure mode).
  useEffect(() => {
    const suppress = (e: DragEvent) => {
      e.preventDefault();
    };
    document.addEventListener('dragover', suppress);
    document.addEventListener('drop', suppress);
    return () => {
      document.removeEventListener('dragover', suppress);
      document.removeEventListener('drop', suppress);
    };
  }, []);

  const addFiles = useCallback(
    (incoming: FileList | File[] | null) => {
      if (disabled || !incoming) return;
      const list = Array.from(incoming);
      if (list.length === 0) return;
      setFiles((prev) => {
        const { accepted, rejected } = screenFiles(prev, list);
        setAttachError(rejected.length ? rejected.join(' ') : null);
        return accepted;
      });
    },
    [disabled],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachError(null);
  }, []);

  const clear = useCallback(() => {
    setFiles([]);
    setAttachError(null);
  }, []);

  const onPaste = useCallback(
    (e: ReactClipboardEvent<HTMLElement>) => {
      const pasted = clipboardFiles(e.clipboardData);
      if (pasted.length === 0) return; // plain text paste — leave it alone
      e.preventDefault();
      addFiles(pasted);
    },
    [addFiles],
  );

  const onDragEnter = useCallback((e: ReactDragEvent<HTMLElement>) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  }, []);

  const onDragOver = useCallback((e: ReactDragEvent<HTMLElement>) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDragLeave = useCallback((e: ReactDragEvent<HTMLElement>) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: ReactDragEvent<HTMLElement>) => {
      if (!dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  return {
    files,
    attachError,
    isDragging,
    addFiles,
    removeFile,
    clear,
    onPaste,
    dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
